import axios from 'axios';

const PEXELS_API_KEY = process.env.REACT_APP_PEXELS_API_KEY || 'UPxci0Gt4cv8vgzndKm8PrY4sFRe9GZ4lobIKAX9EdSvvzZqfuFEyySZ';
const PEXELS_API_URL = 'https://api.pexels.com/v1';
const PEXELS_VIDEOS_URL = 'https://api.pexels.com/videos';

interface PexelsPhotoSrc {
  original: string;
  large2x: string;
  large: string;
  medium: string;
  small: string;
  portrait: string;
  landscape: string;
  tiny: string;
}

export interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  url: string;
  photographer: string;
  photographer_url: string;
  photographer_id: number;
  avg_color: string;
  src: PexelsPhotoSrc;
  liked: boolean;
  alt: string;
}

export interface PexelsPhotoSearchResponse {
  page: number;
  per_page: number;
  photos: PexelsPhoto[];
  total_results: number;
  next_page?: string;
}

interface PexelsVideoFile {
  id: number;
  quality: string;
  file_type: string;
  width: number;
  height: number;
  link: string;
}

interface PexelsVideoPicture {
  id: number;
  picture: string;
}

export interface PexelsVideo {
  id: number;
  width: number;
  height: number;
  url: string;
  image: string;
  duration: number;
  user: {
    id: number;
    name: string;
    url: string;
  };
  video_files: PexelsVideoFile[];
  video_pictures: PexelsVideoPicture[];
}

export interface PexelsVideoSearchResponse {
  page: number;
  per_page: number;
  videos: PexelsVideo[];
  total_results: number;
  next_page?: string;
}

const pexelsApi = axios.create({
  baseURL: PEXELS_API_URL,
  headers: {
    Authorization: PEXELS_API_KEY,
  },
});

const pexelsVideosApi = axios.create({
  baseURL: PEXELS_VIDEOS_URL,
  headers: {
    Authorization: PEXELS_API_KEY,
  },
});

export const pexelsApiService = {
  searchPhotos: async (query: string, page: number = 1, perPage: number = 15): Promise<PexelsPhotoSearchResponse> => {
    const response = await pexelsApi.get('/search', {
      params: { query, page, per_page: perPage },
    });
    return response.data;
  },

  getPopularPhotos: async (page: number = 1, perPage: number = 15): Promise<PexelsPhotoSearchResponse> => {
    const response = await pexelsApi.get('/curated', {
      params: { page, per_page: perPage },
    });
    return response.data;
  },

  downloadPhoto: async (photo: PexelsPhoto): Promise<Blob> => {
    const response = await axios.get(photo.src.original, {
      responseType: 'blob',
    });
    return response.data;
  },

  searchVideos: async (query: string, page: number = 1, perPage: number = 15): Promise<PexelsVideoSearchResponse> => {
    const response = await pexelsVideosApi.get('/search', {
      params: { query, page, per_page: perPage },
    });
    return response.data;
  },

  getPopularVideos: async (page: number = 1, perPage: number = 15): Promise<PexelsVideoSearchResponse> => {
    const response = await pexelsVideosApi.get('/popular', {
      params: { page, per_page: perPage },
    });
    return response.data;
  },

  downloadVideo: async (video: PexelsVideo): Promise<Blob> => {
    // Find the highest quality video link
    const bestQualityVideo = video.video_files.reduce((best: any, current: any) => {
      if (!best) return current;
      return current.width > best.width ? current : best;
    }, video.video_files[0]); // Initialize with the first video file

    if (!bestQualityVideo) {
      throw new Error('No video files found for download.');
    }

    const response = await axios.get(bestQualityVideo.link, {
      responseType: 'blob',
    });
    return response.data;
  },
};